/**
 * ATTEST-UX-1 (D1) — which fields are required, in one place.
 *
 * The brief's constraint is the whole design: *drive the "required" set off the
 * same contract the engine already enforces, not a separately hand-maintained
 * list. Stars must match what the gate actually refuses on, or they drift.*
 *
 * So there is exactly one derivation here and two views of it:
 *
 *  - `requiredFieldIds` — everything that wears an asterisk. The steps render
 *    `<RequiredMark field={id}/>`, which stamps `data-required-for={id}` into the
 *    DOM, and a test asserts the rendered set equals this function's output. A
 *    star added by hand to a field this function does not name fails that test.
 *  - `missingRequiredFields` — the subset that is *presently* empty, which is
 *    what `PreflightChecklist` lists on Step 5. It cannot name a field the star
 *    set does not contain, because both come from `buildEntries`.
 *
 * Where each entry comes from in the engine, so the mapping is auditable:
 *
 * | field                                   | engine refusal                        |
 * |-----------------------------------------|---------------------------------------|
 * | `attest-letter-type`, `requested-items` | `NO_REQUEST_ITEMS` (engagement_router)|
 * | `input-<key>`                           | `_check_inputs` → ATTEST_INPUT_MISSING|
 * | `assertion-<i>` / `statement-text-<i>`  | `_check_allow_list` → NO_STATEMENTS   |
 * | `statement-evidence-<i>`                | `_check_evidence`                     |
 * | `client-consent`                        | `_check_consent` → CLIENT_CONSENT_MISSING |
 * | `attested-no-interest`                  | `NO_INDEPENDENCE_ATTESTATION`         |
 * | `licensed-for-attest`, `peer-review-current` | `check_issuer_eligibility`       |
 *
 * Two entries are honest exceptions, marked as such rather than quietly
 * included: `subject-entity` and `addressee` are not refused on emptiness by a
 * gate of their own — the API accepts `""`. Subject entity is required in
 * substance (independence cannot be assessed without it, so an empty one earns
 * `NO_INDEPENDENCE_ATTESTATION`), and the addressee is interpolated into every
 * letter type's body. The brief names both; they are starred, and the reason is
 * recorded here rather than dressed up as a gate.
 */
import type { AttestPolicy, LetterTypeSummary } from '@/components/attest/api'
import { EMPTY_CLASSIFY_FORM } from '@/components/attest/classifyQuestions'
import type { ClassifyFormState } from '@/components/attest/classifyQuestions'
import { EMPTY_LETTER_FORM } from '@/components/attest/formState'
import type { IssuerFormState, LetterFormState } from '@/components/attest/formState'
import type { AttestStepKey } from '@/components/attest/refusalGuidance'

/**
 * Placeholder styling for *example* text (D1's related fix). Greyed example
 * lines read as entered content — the reported `NO_REQUEST_ITEMS` confusion was
 * a CPA seeing two sample asks in the box and believing the field was filled.
 * Italic plus a lighter grey, and callers prefix the text with "e.g.".
 */
export const EXAMPLE_PLACEHOLDER = 'placeholder:italic placeholder:text-muted-foreground/70'

const STEP_LABELS: Record<AttestStepKey, string> = {
  classify: '1. Classify the request',
  scope: '2. What may be said',
  details: '3. Letter and statements',
  independence: '4. Independence',
  letter: '5. Draft and download',
}

export interface RequiredFieldContext {
  /** The chosen type id — `''` before one is picked. */
  letterTypeId: string
  /** Resolved to the chosen basis; null before a type is picked. */
  letterType: LetterTypeSummary | null
  classify: ClassifyFormState
  form: LetterFormState
  issuer: IssuerFormState
  /** Needed only to tell whether the type demands an attest-eligible issuer. */
  policy: AttestPolicy | null
}

export interface RequiredFieldEntry {
  /** The control's DOM id, and the `data-required-for` value on its marker. */
  id: string
  step: AttestStepKey
  /** Empty → this sentence appears in the Step-5 preflight. */
  missingText: string
  satisfied: boolean
  /**
   * Starred, but not yet judged. A wholly blank statement row is the only case:
   * it wears the stars that tell the CPA what a statement needs, but flagging it
   * would flag the empty form they just opened — the same filter
   * `buildLetterRequest` applies before sending.
   */
  dormant?: boolean
}

const filled = (v: string | undefined) => Boolean((v ?? '').trim())

/**
 * Does this engagement demand a licensed, peer-reviewed issuer?
 *
 * Exported so `independencePreflight.ts` (PREFLIGHT-BASIS-AWARE-1) can gate
 * `previewEligibility` on the same derivation instead of re-deriving the
 * policy lookup a second time — two independent derivations of the same
 * fact is exactly the drift this module exists to prevent, and it is what
 * let `previewEligibility` fall out of sync with this one in the first
 * place: it never consulted this check at all.
 */
export function engagementRequiresAttestEligibleIssuer(
  letterType: LetterTypeSummary | null,
  policy: AttestPolicy | null,
): boolean {
  const engagementType = letterType?.engagement_type
  if (!engagementType) return false
  // No policy loaded means we do not know. Say nothing rather than guess: an
  // invented star is exactly the drift this module exists to prevent.
  return Boolean(policy?.engagement_types?.[engagementType]?.requires_attest_eligible_issuer)
}

/** Does this type's engagement demand a licensed, peer-reviewed issuer? */
function needsAttestEligibleIssuer(ctx: RequiredFieldContext): boolean {
  return engagementRequiresAttestEligibleIssuer(ctx.letterType, ctx.policy)
}

function buildEntries(ctx: RequiredFieldContext): RequiredFieldEntry[] {
  const { letterType, classify, form, issuer } = ctx
  const entries: RequiredFieldEntry[] = []
  const push = (
    id: string, step: AttestStepKey, satisfied: boolean, missingText: string,
    dormant = false,
  ) => entries.push({ id, step, satisfied, missingText, dormant })

  push('attest-letter-type', 'classify', Boolean(ctx.letterTypeId),
    'No letter type has been chosen. Nothing else can be checked until one is.')
  push('requested-items', 'classify',
    classify.requestedItemsText.split('\n').some(l => l.trim()),
    'The requester’s ask is empty. The router refuses a request with no items '
    + '(NO_REQUEST_ITEMS) — the greyed example lines in that box are not entered text.')

  if (!letterType) return entries

  push('subject-entity', 'details', filled(form.subjectEntity),
    'The subject entity has not been named. Independence cannot be assessed without it.')
  push('addressee', 'details', filled(form.addressee),
    'The letter has no addressee.')

  for (const input of letterType.required_inputs ?? []) {
    push(`input-${input.key}`, 'details', filled(form.inputs[input.key]),
      `${input.label} is required by this letter type and is empty.`)
  }

  form.statements.forEach((s, i) => {
    const blank = !filled(s.assertion_id) && !filled(s.text)
    const label = s.assertion_id.trim() || `Statement ${i + 1}`
    push(`assertion-${i}`, 'details', filled(s.assertion_id),
      `${label} has no assertion selected.`, blank)
    push(`statement-text-${i}`, 'details', filled(s.text),
      `${label} has no text.`, blank)
    push(`statement-evidence-${i}`, 'details',
      s.evidence.some(e => filled(e.reference)),
      `${label} states a fact with no source behind it. Attach evidence.`, blank)
  })

  push('client-consent', 'details', form.clientConsentOnFile,
    'Written client consent is not recorded. The engine refuses without it '
    + '(IRC §7216 / ET 1.700).')

  // The brief's either/or: attest to no impairing interest, *or* declare one.
  // Once an interest is declared the star correctly disappears — attesting then
  // would contradict the declaration, which the engine treats as the worse
  // finding of the two.
  const declared = issuer.declaredInterests.some(
    i => filled(i.subject_entity) && filled(i.interest_type),
  )
  if (!declared) {
    push('attested-no-interest', 'independence', issuer.attestedNoImpairingInterest,
      'The firm has neither attested to independence nor declared an interest.')
  }

  if (needsAttestEligibleIssuer(ctx)) {
    push('licensed-for-attest', 'independence', issuer.licensedForAttest,
      'This letter type requires an attest-eligible issuer, and the firm has not '
      + 'confirmed it is licensed for attest work.')
    push('peer-review-current', 'independence', issuer.peerReviewCurrent,
      'This letter type requires an attest-eligible issuer, and the firm has not '
      + 'confirmed its peer review is current.')
  }

  return entries
}

/** Every field that wears an asterisk, in wizard order. */
export function requiredFieldIds(ctx: RequiredFieldContext): string[] {
  return buildEntries(ctx).map(e => e.id)
}

/** The subset presently empty — what Step 5 lists. Always a subset of the above. */
export function missingRequiredFields(ctx: RequiredFieldContext): RequiredFieldEntry[] {
  return buildEntries(ctx).filter(e => !e.satisfied && !e.dormant)
}

/**
 * The steps whose own required fields are all filled — the green tick on the
 * strip (Bundle 2 item 6). Third view of `buildEntries`, for the same reason as
 * the first two: a done-state derived separately would eventually disagree with
 * the stars, and a tick that disagrees with the form is worse than no tick.
 *
 * Two deliberate differences from `missingRequiredFields`:
 *
 * - **`dormant` does not count as satisfied here.** An untouched statement row
 *   is correctly not *flagged* — the CPA has only just opened the form — but it
 *   is equally not done, and calling step 3 finished with no statement written
 *   is the one lie this mark could tell.
 * - **A step with no entries never goes green.** Step 2 states no facts and
 *   step 5 collects nothing, so "all their requirements are met" is vacuously
 *   true for both from the first render. A tick that is on before the CPA
 *   arrives means nothing at all; these two simply never carry one.
 *
 * This is a completeness mark, not permission to issue. Consent, independence,
 * the deny list and the evidence gates are all still decided by the server, and
 * five ticks do not promise a letter.
 */
export function completedSteps(ctx: RequiredFieldContext): Set<AttestStepKey> {
  const done = new Set<AttestStepKey>()
  const unfinished = new Set<AttestStepKey>()
  for (const e of buildEntries(ctx)) {
    if (e.satisfied) done.add(e.step)
    else unfinished.add(e.step)
  }
  unfinished.forEach(s => done.delete(s))
  return done
}

/**
 * Which of step 4's fields wear a star as things stand.
 *
 * `IndependenceStep` holds the issuer and the letter type but not the classify
 * or letter forms, and it should not be handed them just to draw an asterisk.
 * The step-4 entries depend on none of that, so the other halves of the context
 * are stubbed and the answer still comes out of `buildEntries` — the point being
 * that step 4's stars and step 5's list cannot disagree.
 */
export function independenceRequiredFields(
  letterType: LetterTypeSummary | null,
  issuer: IssuerFormState,
  policy: AttestPolicy | null,
): Set<string> {
  const entries = buildEntries({
    letterTypeId: letterType?.type_id ?? '',
    letterType,
    classify: EMPTY_CLASSIFY_FORM,
    form: EMPTY_LETTER_FORM,
    issuer,
    policy,
  })
  return new Set(entries.filter(e => e.step === 'independence').map(e => e.id))
}

export function stepLabel(step: AttestStepKey): string {
  return STEP_LABELS[step]
}

/**
 * The asterisk itself. `data-required-for` is what the anti-drift test reads, so
 * it carries the field id rather than being decorative.
 *
 * It is no longer red (Bundle 2 item 6). Red on these screens now means one
 * thing — a hard stop the server will refuse on — and a field that is merely
 * *required* is not that: it is a field the CPA has not filled in yet, on a form
 * they opened four seconds ago. Eleven red marks on an untouched form spend the
 * colour before anything has actually gone wrong, and by the time something has,
 * the refusal wall is competing with a form full of the same red. Neutral here,
 * amber for a blocked continue, red for the refusal itself.
 *
 * Put it inside a `<Label>` and give that label `className="block"`. The shared
 * `Label` is `flex items-center gap-2`, which makes the label *text* an
 * anonymous flex item that shrinks to fill the row — so on any label long enough
 * to fill it, the star detaches and lands against the right edge, orphaned from
 * the words it qualifies. At 375px that is most of them. `block` (tailwind-merge
 * drops the `flex`) lets the text and the star flow as one line box again, which
 * is all these labels ever needed.
 */
export function RequiredMark({ field }: { field: string }) {
  return (
    <>
      <span
        data-required-for={field}
        className="ml-0.5 font-semibold text-muted-foreground"
        aria-hidden="true"
      >
        *
      </span>
      <span className="sr-only"> (required)</span>
    </>
  )
}

/** The top-of-form note the brief asks for, on every step that has markers. */
export function RequiredFieldsNote() {
  return (
    <p
      data-testid="required-fields-note"
      className="text-xs font-medium text-muted-foreground"
    >
      Fields marked with an asterisk (
      <span className="font-semibold" aria-hidden="true">*</span>
      ) are required.
    </p>
  )
}
